/**
 * Editable fields shared by the per-row edit modal and the bulk-update modal.
 * Mirrors the AS/400 OPE84440D screen (underlined = input-capable).
 * Excludes auto-calculated fields (claim_amount, UOM qualifiers, outbound_flag).
 */

export interface EditableFieldDef {
  /** snake_case DB column name (matches sd_e844d / toJSON() keys) */
  key: string;
  label: string;
  inputMode: 'decimal' | 'text';
  section: 'pricing' | 'reference' | 'location' | 'identity' | 'item';
}

export const EDITABLE_FIELDS: EditableFieldDef[] = [
  // Pricing — OPE84440 recalculates claim_amount from these inputs
  {
    key: 'unit_price_wholesale',
    label: 'Wholesale Price',
    inputMode: 'decimal',
    section: 'pricing',
  },
  {
    key: 'unit_price_quoted',
    label: 'Quoted Price',
    inputMode: 'decimal',
    section: 'pricing',
  },
  {
    key: 'buyer_cost',
    label: 'Buyer Cost',
    inputMode: 'decimal',
    section: 'pricing',
  },
  {
    key: 'core_cost_override',
    label: 'Cost Override',
    inputMode: 'decimal',
    section: 'pricing',
  },
  {
    key: 'invoiced_amount',
    label: 'Invoice Cost',
    inputMode: 'decimal',
    section: 'pricing',
  },
  // Reference
  {
    key: 'supplier_contract_no',
    label: 'Contract No',
    inputMode: 'text',
    section: 'reference',
  },
  {
    key: 'claim_number',
    label: 'Claim Number',
    inputMode: 'text',
    section: 'reference',
  },
  {
    key: 'transfer_type_code',
    label: 'Transfer Type',
    inputMode: 'text',
    section: 'reference',
  },
  {
    key: 'invoice_date',
    label: 'Invoice Date',
    inputMode: 'text',
    section: 'reference',
  },
  // Location — location codes are editable, names are display-only
  {
    key: 'selling_location',
    label: 'Sell Loc',
    inputMode: 'text',
    section: 'location',
  },
  {
    key: 'shipping_location',
    label: 'Ship Loc',
    inputMode: 'text',
    section: 'location',
  },
  {
    key: 'supplier_sell_location',
    label: 'Supp. Sell Loc',
    inputMode: 'text',
    section: 'location',
  },
  {
    key: 'sold_to_no',
    label: 'Sold-To No',
    inputMode: 'text',
    section: 'location',
  },
  {
    key: 'ship_to_no',
    label: 'Ship-To No',
    inputMode: 'text',
    section: 'location',
  },
  // Identity
  {
    key: 'trading_partner',
    label: 'Trading Partner',
    inputMode: 'text',
    section: 'identity',
  },
  {
    key: 'rebate_type',
    label: 'Rebate Type',
    inputMode: 'text',
    section: 'identity',
  },
  {
    key: 'ship_type',
    label: 'Ship Type',
    inputMode: 'text',
    section: 'identity',
  },
  {
    key: 'buyer_product_no',
    label: 'Buyer Product No (rdbpc)',
    inputMode: 'text',
    section: 'item',
  },
  {
    key: 'upc_code',
    label: 'UPC Code',
    inputMode: 'text',
    section: 'item',
  },
];

export const EDITABLE_KEYS = new Set(EDITABLE_FIELDS.map((f) => f.key));

export const DECIMAL_FIELDS = new Set(
  EDITABLE_FIELDS.filter((f) => f.inputMode === 'decimal').map((f) => f.key),
);

/**
 * A complete decimal number: optional sign, digits, at most one decimal point.
 * Scientific notation is rejected — these are prices, and `1e3` is far more
 * likely a typo than an intent to write 1000.
 */
const COMPLETE_DECIMAL = /^[+-]?(?:\d+(?:\.\d*)?|\.\d+)$/;

export function isCompleteDecimal(value: string): boolean {
  const trimmed = value.trim();
  return COMPLETE_DECIMAL.test(trimmed) && Number.isFinite(Number(trimmed));
}

export type DecimalInputResult =
  { ok: true; value: number | null } | { ok: false };

/**
 * Parse a decimal FOR PERSISTENCE. Blank is a deliberate clear (`null`);
 * anything that isn't a complete decimal is `{ ok: false }` and the caller
 * MUST refuse to save.
 *
 * These fields are plain text inputs — `inputMode="decimal"` is only a mobile
 * keyboard hint — so a paste can carry anything. `parseFloat` silently accepts
 * a valid *prefix*, which is how a mistyped price becomes a plausible wrong
 * one: `"77.9abc"` → 77.9, `"1.2.3"` → 1.2, and a spreadsheet paste of
 * `"1,234.50"` → **1**. Each then flows into OPE84440's claim recalculation and
 * out to the supplier on the 844.
 *
 * The result is a discriminated union rather than a nullable number on purpose:
 * coercing an unparseable value to `null` would CLEAR the price — turning a
 * typo into a destructive write, which is worse than the truncation it fixes.
 * Invalid input must block the save, never rewrite the field.
 */
export function parseDecimalInput(value: string): DecimalInputResult {
  const trimmed = value.trim();
  if (!trimmed) return { ok: true, value: null };
  if (!isCompleteDecimal(trimmed)) return { ok: false };
  return { ok: true, value: Number(trimmed) };
}

/**
 * Lenient parse for DISPLAY ONLY (live previews, formatting).
 *
 * Never use this to build an update payload — see `parseDecimalInput` for why.
 */
export function parseNullableDecimal(value: string): number | null {
  if (!value) return null;
  const num = parseFloat(value);
  return Number.isFinite(num) ? num : null;
}
